import React, { ComponentProps, forwardRef, ReactElement, useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import _MetaSearchTileBase from './_MetaSearchTileBase'
import _MetaSearchTileContent from './_MetaSearchTileContent'
import _MetaSearchTileContentPrimary from './_MetaSearchTileContentPrimary'
import _MetaSearchTileContentSecondary from './_MetaSearchTileContentSecondary'
import _MetaSearchTileHero from './_MetaSearchTileHero'
import _MetaSearchTilePricing from './_MetaSearchTilePricing'
import { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import Divider from 'LuxKit/Divider'

type InheritedSearchTileBaseProps = Pick<ComponentProps<typeof _MetaSearchTileBase>, 'state' | 'banner'>
type InheritedSearchTileContentProps = Pick<ComponentProps<typeof _MetaSearchTileContent>, 'bottomExtension' | 'topExtension'>
type InheritedSearchTileContentPrimaryProps = Pick<ComponentProps<typeof _MetaSearchTileContentPrimary>, 'cancellationPolicyLabel' | 'features' | 'labels' | 'overline' | 'subtitle' | 'title'>
type InheritedSearchTileHeroProps = Pick<ComponentProps<typeof _MetaSearchTileHero>, 'bookmarkAction' | 'images' | 'onImageChange' | 'onImageLoad' | 'productLabel'>

export interface SearchTileLogo {
  full: App.Image;
  compact: App.Image
}

interface Props extends InheritedSearchTileBaseProps, InheritedSearchTileContentProps, InheritedSearchTileContentPrimaryProps, InheritedSearchTileHeroProps, Utils.PropsWithDataAttributes {
  logo?: SearchTileLogo
  /**
   * Price point rows and stacks
   */
  primaryPricePoint?: ReactElement | false
  secondaryPricePoint?: ReactElement | false
  className?: string
  offer: App.AnyOffer
  actionSecondary: ReactElement | false
  actionPrimary: ReactElement | false
  showPrimaryPrice?: boolean
  hideSecondaryPrice?: boolean
  primaryVariant?: 'default' | 'meta'
  secondaryVariant?: 'default' | 'meta'
}

const MetaSearchTile = forwardRef<HTMLDivElement, Props>(function SearchTile(
  {
    state,
    banner,
    bookmarkAction,
    cancellationPolicyLabel,
    features,
    images,
    labels,
    logo,
    overline,
    productLabel,
    primaryPricePoint,
    secondaryPricePoint,
    actionPrimary,
    actionSecondary,
    title,
    className,
    subtitle,
    bottomExtension,
    topExtension,
    onImageChange,
    onImageLoad,
    offer,
    showPrimaryPrice,
    hideSecondaryPrice,
    primaryVariant,
    secondaryVariant,
    ...dataAttributes
  },
  ref,
) {
  const { type: offerType, productType } = offer
  const listId = useMemo(() => uuidv4(), [])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    onImageChange?.(index, image)
    if (offer && image) {
      Analytics.trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'search-tile',
          listId,
          offer,
          undefined,
        ),
      )
    }
  }, [onImageChange, offer, listId])
  return <_MetaSearchTileBase
    {...dataAttributes}
    data-product={productType}
    data-offer={offerType}
    state={state}
    banner={banner}
    className={className}
    ref={ref}
  >
    <_MetaSearchTileHero
      bookmarkAction={bookmarkAction}
      images={images}
      productLabel={productLabel}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
    <_MetaSearchTileContent bottomExtension={bottomExtension} topExtension={topExtension}>
      <_MetaSearchTileContentPrimary
        cancellationPolicyLabel={cancellationPolicyLabel}
        features={features}
        labels={labels}
        overline={overline}
        title={title}
        subtitle={subtitle}
      />
      <_MetaSearchTileContentSecondary>

        {showPrimaryPrice && <_MetaSearchTilePricing hideLogoAndTitle={hideSecondaryPrice} action={actionPrimary} variant={primaryVariant}>
          {primaryPricePoint}
        </_MetaSearchTilePricing>}
        {!hideSecondaryPrice && showPrimaryPrice && <Divider kind="primary" direction="vertical" />}
        {!hideSecondaryPrice && <_MetaSearchTilePricing hideLogoAndTitle={!showPrimaryPrice} action={actionSecondary} variant={secondaryVariant}>
          {secondaryPricePoint}
        </_MetaSearchTilePricing>}
      </_MetaSearchTileContentSecondary>
    </_MetaSearchTileContent>
  </_MetaSearchTileBase>
})
export default MetaSearchTile
